import React, { useState } from "react";
import { Protected } from "../../layout/Protected";
import AjaxService from "../../platform/ajax/AjaxService";

export function TechDrawExportImportPage() {
    const [file, setFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [importMessage, setImportMessage] = useState<string | null>(null);
    const [importStatus, setImportStatus] = useState<'success' | 'error' | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
        // Clear previous messages when selecting a new file
        setImportMessage(null);
        setImportStatus(null);
    };

    const handleExport = () => {
        AjaxService.downloadAuthenticatedFile('/api/tech-draws-admin/export', 'tech-draws-export.zip')
            .catch(error => {
                console.error('Export error:', error);
                alert('Błąd podczas eksportu rysunków');
            });
    };

    const handleImport = async () => {
        if (!file) return;

        setIsImporting(true);
        setImportMessage(null);
        setImportStatus(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const result = await AjaxService.postFormData('/api/tech-draws-admin/import', formData);

            if (result.status === 'success') {
                setImportStatus('success');
                setImportMessage(result.message || 'Import zakończony pomyślnie');
                setFile(null);
            } else {
                setImportStatus('error');
                setImportMessage(result.message || 'Błąd podczas importu');
            }
        } catch (error) {
            setImportStatus('error');
            setImportMessage('Błąd połączenia podczas importu');
            console.error('Import error:', error);
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <Protected privilege="PERM_DATA_ADMINISTRATION">
            <div>
                {/* Page Header */}
                <div className="card mb-4">
                    <div className="card-body">
                        <h2>Eksport/Import rysunków technicznych</h2>
                        <p className="text-muted mb-0">
                            Ta strona umożliwia pełny eksport i import wszystkich rysunków technicznych przechowywanych w systemie.
                        </p>
                    </div>
                </div>

                {/* Export Section */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h4 className="card-title">Eksport</h4>
                        </div>
                        <div className="card-body">
                            <p>
                                Eksportuje wszystkie rysunki techniczne do pliku ZIP zawierającego metadane i pliki rysunków.
                            </p>
                            <button 
                                className="btn btn-primary"
                                onClick={handleExport}
                            >
                                <i className="mdi mdi-download me-2"></i>
                                Pobierz wszystkie rysunki (ZIP)
                            </button>
                        </div>
                    </div>

                    {/* Import Section */}
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title">Import</h4>
                        </div>
                        <div className="card-body">
                            <div className="alert alert-warning">
                                <strong>Uwaga:</strong> Import całkowicie zastępuje wszystkie istniejące rysunki techniczne w systemie.
                                Ta operacja jest nieodwracalna.
                            </div>

                            <div className="mb-3">
                                <label htmlFor="importFile" className="form-label">
                                    Wybierz plik ZIP z rysunkami do importu:
                                </label>
                                <input 
                                    id="importFile"
                                    type="file" 
                                    className="form-control"
                                    accept=".zip"
                                    onChange={handleFileChange}
                                    disabled={isImporting}
                                />
                            </div>

                            {file && (
                                <div className="mb-3">
                                    <div className="card bg-light">
                                        <div className="card-body">
                                            <h6>Szczegóły pliku:</h6>
                                            <ul className="list-unstyled mb-0">
                                                <li><strong>Nazwa:</strong> {file.name}</li>
                                                <li><strong>Typ:</strong> {file.type}</li>
                                                <li><strong>Rozmiar:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {importMessage && (
                                <div className={`alert ${importStatus === 'success' ? 'alert-success' : 'alert-danger'} mb-3`}>
                                    <i className={`mdi ${importStatus === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'} me-2`}></i>
                                    {importMessage}
                                </div>
                            )}

                            {file && (
                                <button
                                    onClick={handleImport}
                                    className="btn btn-danger"
                                    disabled={isImporting}
                                >
                                    {isImporting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Importowanie...
                                        </>
                                    ) : (
                                        <>
                                            <i className="mdi mdi-upload me-2"></i>
                                            Importuj rysunki (zastąp wszystkie)
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
            </div>
        </Protected>
    );
}